import jsPDF from "jspdf"
import "jspdf-autotable"
import { deliverPdf } from "./helpers/pdfDelivery.js"
import { drawPdfLogo } from "./helpers/pdfLogo.js"

const formatCurrency = (value) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0)

const formatDate = (dateString) => {
  if (!dateString) return "-"
  const dateObj = new Date(dateString)
  if (Number.isNaN(dateObj.getTime())) return String(dateString)
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(dateObj)
}

export const generarPdfClientePaqueteMovimientos = ({
  movimientos = [],
  filtros = {},
  usuario = "Usuario",
  logoBase64 = null,
  deliveryContext = null,
}) => {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 20

  const PAL = {
    primary: [91, 91, 95],
    ink: [33, 33, 33],
    muted: [117, 117, 117],
    inkLight: [158, 158, 158],
    bgSoft: [245, 247, 250],
    success: [76, 175, 80],
    error: [244, 67, 54],
  }

  const pagos = movimientos.filter((movimiento) => movimiento.tipo === "pago")
  const reversiones = movimientos.filter((movimiento) => movimiento.tipo === "reversion")
  const totalPagos = pagos.reduce((sum, movimiento) => sum + (Number(movimiento.monto) || 0), 0)
  const totalReversiones = reversiones.reduce((sum, movimiento) => sum + (Number(movimiento.monto) || 0), 0)

  drawPdfLogo({ doc, logoBase64, x: margin, y: margin - 4, maxW: 80, maxH: 36 })

  let y = margin + 12

  doc.setFont("helvetica", "bold")
  doc.setFontSize(16)
  doc.setTextColor(...PAL.primary)
  doc.text("MOVIMIENTOS CLIENTE GRUPOS", pageWidth / 2, y, { align: "center" })
  y += 18

  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  doc.setTextColor(...PAL.inkLight)
  doc.text(
    `Fecha de generación: ${new Date().toLocaleString("es-AR")} | Usuario: ${usuario}`,
    pageWidth - margin,
    y,
    { align: "right" }
  )
  y += 24

  doc.setFont("helvetica", "bold")
  doc.setFontSize(11)
  doc.setTextColor(...PAL.ink)
  doc.text("Filtros aplicados", margin, y)
  y += 14

  doc.setDrawColor(...PAL.primary)
  doc.setLineWidth(1)
  doc.line(margin, y, pageWidth - margin, y)
  y += 12

  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  doc.setTextColor(...PAL.muted)
  doc.text(
    `Fecha desde: ${filtros.fechaDesde || "-"} | Fecha hasta: ${filtros.fechaHasta || "-"}`,
    margin,
    y,
    { maxWidth: pageWidth - margin * 2 }
  )
  y += 20

  doc.setFont("helvetica", "bold")
  doc.setFontSize(11)
  doc.setTextColor(...PAL.ink)
  doc.text("Resumen", margin, y)
  y += 14

  doc.setDrawColor(...PAL.primary)
  doc.line(margin, y, pageWidth - margin, y)
  y += 12

  doc.setFillColor(...PAL.bgSoft)
  doc.roundedRect(margin, y - 5, pageWidth - margin * 2, 24, 3, 3, "F")
  doc.setFont("helvetica", "bold")
  doc.setFontSize(9)
  doc.setTextColor(...PAL.ink)
  doc.text(
    `Movimientos: ${movimientos.length} | Pagos: ${pagos.length} (${formatCurrency(totalPagos)}) | Reversiones: ${reversiones.length} (${formatCurrency(totalReversiones)})`,
    margin + 8,
    y + 6
  )
  y += 30

  const body =
    movimientos.length === 0
      ? [["Sin movimientos registrados", "", "", "", ""]]
      : movimientos.map((movimiento) => [
          formatDate(movimiento.fecha),
          movimiento.tipo === "reversion" ? "Reversión" : "Pago",
          movimiento.cuotaNro != null ? String(movimiento.cuotaNro) : "-",
          movimiento.descripcion || "-",
          formatCurrency(movimiento.monto),
        ])

  doc.autoTable({
    startY: y,
    head: [["Fecha", "Tipo", "Nro Cuota", "Descripción", "Monto"]],
    body,
    theme: "grid",
    headStyles: {
      fillColor: PAL.primary,
      fontSize: 9,
      fontStyle: "bold",
      halign: "center",
      textColor: [255, 255, 255],
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 4,
      textColor: PAL.ink,
    },
    alternateRowStyles: movimientos.length > 0 ? { fillColor: [250, 250, 250] } : undefined,
    columnStyles: {
      0: { cellWidth: 78, halign: "center" },
      1: { cellWidth: 70, halign: "center" },
      2: { cellWidth: 70, halign: "center" },
      3: { cellWidth: 420, overflow: "linebreak" },
      4: { cellWidth: 95, halign: "right" },
    },
    margin: { left: margin, right: margin },
    didParseCell: (data) => {
      if (data.section !== "body" || movimientos.length === 0) return
      const movimiento = movimientos[data.row.index]
      if (!movimiento) return
      if (data.column.index === 1 || data.column.index === 4) {
        data.cell.styles.textColor = movimiento.tipo === "reversion" ? PAL.error : PAL.success
        data.cell.styles.fontStyle = "bold"
      }
    },
    didDrawPage: (data) => {
      doc.setFont("helvetica", "normal")
      doc.setFontSize(8)
      doc.setTextColor(...PAL.inkLight)
      doc.text(`Página ${data.pageNumber}`, pageWidth / 2, pageHeight - 20, { align: "center" })
    },
  })

  const fileName = `movimientos-cliente-grupos-${new Date().toISOString().slice(0, 10)}.pdf`
  doc.setProperties({
    title: fileName,
    subject: "Movimientos Cliente Grupos",
    creator: "Sistema",
  })

  deliverPdf({ doc, fileName, targetWindow: deliveryContext?.targetWindow || null })
  return true
}
